import { Controller, useFormContext, useWatch } from "react-hook-form";
import Grid from "@mui/material/Grid2";
import { Checkbox, FormGroup } from "@cofano/cck";
import { intl } from "LocaleWrapper";
import TierUnitSelect from "view/calculationmethods/components/select/TierUnitSelect";
import { PayableOver, TierUnit, TierPriceUnit, ReferenceRateUnit, AgreementTypeEnum } from "__generated__/graphql";
import CargoQuantityUnitSelect from "view/calculationmethods/components/select/CargoQuantityUnitSelect";
import PayableOverSelect from "view/calculationmethods/components/select/PayableOverSelect";
import ApplicableAgreementsSelect from "view/calculationmethods/components/select/ApplicableAgreementsSelect";
import AgreementTypeSelect from "view/agreements/components/selects/AgreementTypeSelect";
import { Fragment, ReactNode, useEffect } from "react";
import FormField from "components/cck/FormField";
import { CalculationMethodFormByTypeProps } from "@schemas/calculationmethods/schema";
import TierForm from "view/calculationmethods/components/form/TierForm";
import WaterLevelSelect from "view/waterlevels/components/selects/WaterLevelSelect";
import useRequest from "components/hooks/useRequest";
import ReferenceRateDateTypeSelect from "view/calculationmethods/components/select/ReferenceRateDateTypeSelect";
import { usePermission } from "components/utils/Permission";
import { WaterLevel } from "view/waterlevels/types/waterlevel";
import type { ApplicableAgreementForm } from "view/calculationmethods/types/calculationmethod";
import { CharterSubtype, CharterType } from "__generated__/enums";
import { AgreementFormInput } from "view/agreements/validation/agreement.zod";

type FormValues = {
	charterType: CharterType;
	charterSubtype: CharterSubtype;
	agreements: AgreementFormInput[];
}

type FormField = {
	id: string;
	render: () => ReactNode;
}

const TieredCalculationMethodForm = ({ agreementTypes, customAgreementTypes, index, currency, hiddenFields }: CalculationMethodFormByTypeProps) => {
	const { isTankmatch } = usePermission();
	const { control, formState: { errors }, setValue } = useFormContext<FormValues>();

	const tierUnit = useWatch({ control, name: `agreements.${index}.calculationMethod.tierUnit` });
	const charterType = useWatch({ control, name: "charterType" });
	const charterSubtype = useWatch({ control, name: "charterSubtype" });
	const tiers = useWatch({ control, name: `agreements.${index}.calculationMethod.tiers` }) ?? [];
	const agreements = useWatch({ control, name: "agreements" }) ?? [];
	const hasPercentageTier = tiers.some(t => t.priceUnit === TierPriceUnit.SurchargePercentage);
	const [ measuringPoints ] = useRequest<WaterLevel[]>({ defaultResponse: [], url: "waterlevels/measuring-points" });

	useEffect(() => {
		if (!hasPercentageTier) {
			setValue(`agreements.${index}.calculationMethod.applicableAgreements`, undefined);
			setValue(`agreements.${index}.calculationMethod.applicableAgreementTypes`, undefined);
		}
	}, [ hasPercentageTier ]);


	const TieredCalculationMethodFormFields: FormField[] = [
		{
			id: "calculationMethod.tierUnit",
			render: () => (
				<Grid size={4}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.tierUnit`}>
						<FormGroup label={intl.formatMessage({ id: "TIER_UNIT" })} required>
							<Controller
								rules={{ required: true }}
								control={control}
								render={({ field }) => (
									<TierUnitSelect{...field} />
								)}
								name={`agreements.${index}.calculationMethod.tierUnit`} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "calculationMethod.cargoQuantityUnit",
			render: () => tierUnit === TierUnit.CargoQuantity && (
				<Grid size={4}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.cargoQuantityUnit`}>
						<FormGroup label={intl.formatMessage({ id: "CARGO_QUANTITY_UNIT" })} overflow="hidden" required>
							<Controller
								name={`agreements.${index}.calculationMethod.cargoQuantityUnit`}
								control={control}
								rules={{ required: true }}
								render={({ field }) => (
									<CargoQuantityUnitSelect {...field} />
								)} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "calculationMethod.payableOver",
			render: () => tierUnit === TierUnit.CargoQuantity && (
				<Grid size={4}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.payableOver`}>
						<FormGroup label={intl.formatMessage({ id: "PAYABLE_OVER" })} overflow="hidden" required>
							<Controller
								name={`agreements.${index}.calculationMethod.payableOver`}
								rules={{ required: true }}
								control={control}
								render={({ field }) => (
									<PayableOverSelect
										{...field}
										options={[ PayableOver.Nominated, PayableOver.Actual ]}
									/>
								)} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "calculationMethod.waterLevelLocation",
			render: () => tierUnit === TierUnit.WaterLevel && (
				<Grid size={4}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.waterLevelLocation`}>
						<FormGroup label={intl.formatMessage({ id: "LOCATION" })} overflow="hidden" required>
							<Controller
								name={`agreements.${index}.calculationMethod.waterLevelLocation`}
								control={control}
								rules={{ required: true }}
								render={({ field }) => (
									<WaterLevelSelect
										value={measuringPoints?.find(m => m.id === field.value)}
										onChange={option => field.onChange(option?.id)} />
								)} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "calculationMethod.referenceRateDateType",
			render: () => tierUnit === TierUnit.GasoilPrice && (
				<Grid size={4}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.referenceRateDateType`}>
						<FormGroup
							label={intl.formatMessage({ id: "REFERENCE_RATE_DATE_TYPE" })}
							overflow="hidden"
							required>
							<Controller
								name={`agreements.${index}.calculationMethod.referenceRateDateType`}
								control={control}
								rules={{ required: true }}
								render={({ field }) => (
									<ReferenceRateDateTypeSelect referenceRateUnit={ReferenceRateUnit.GasolinePrice} {...field} />
								)} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "calculationMethod.cdniCostIncluded",
			render: () => tierUnit === TierUnit.GasoilPrice && (
				<Grid size={4}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.cdniCostIncluded`}>
						<FormGroup label={intl.formatMessage({ id: "CDNI_COST_LABEL" })} overflow="hidden">
							<Controller
								name={`agreements.${index}.calculationMethod.cdniCostIncluded`}
								control={control}
								render={({ field }) => (
									<Checkbox
										{...field}
										label={intl.formatMessage({ id: "CDNI_COST_EXCLUDED" })}
										checked={!field.value}
										value={undefined}
										onChange={e => field.onChange(!e.target.checked)}
									/>
								)} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "calculationMethod.applicableAgreementTypes",
			render: () => hasPercentageTier && (
				<Grid size={4}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.applicableAgreementTypes`}>
						<FormGroup label={intl.formatMessage({ id: "AGREEMENT_TYPES" })} overflow="hidden" required={isTankmatch}>
							<Controller
								name={`agreements.${index}.calculationMethod.applicableAgreementTypes`}
								control={control}
								render={({ field }) => (
									<AgreementTypeSelect
										{...field}
										onChange={(value) => {
											field.onChange(value);
											const selectedTypes = Array.isArray(value) ? value : [];
											const matching = agreements
												.filter((a, i) => i !== index && selectedTypes.includes(a.type as AgreementTypeEnum))
												.map(a => ({ id: a.id, uuid: a.uuid, type: a.type, calculationMethod: a.calculationMethod }));
											setValue(`agreements.${index}.calculationMethod.applicableAgreements`, matching, { shouldDirty: true, shouldValidate: true });
										}}
										agreementTypes={agreementTypes}
										customAgreementTypes={customAgreementTypes}
										getOptionDisabled={option => (option === AgreementTypeEnum.AddressCommission || option === AgreementTypeEnum.Commission)}
										charterType={charterType}
										charterSubtype={charterSubtype}
										multiple={true}
									/>
								)}
							/>
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "calculationMethod.applicableAgreements",
			render: () => hasPercentageTier && (
				<Grid size={4}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.applicableAgreements`}>
						<FormGroup label={intl.formatMessage({ id: "APPLICABLE_AGREEMENTS" })} overflow="hidden" required={!isTankmatch}>
							<Controller
								name={`agreements.${index}.calculationMethod.applicableAgreements`}
								control={control}
								render={({ field }) => (
									<ApplicableAgreementsSelect
										{...field}
										onChange={(value) => field.onChange(
											Array.isArray(value) ? value.map((a: ApplicableAgreementForm) => ({ id: a.id, uuid: a.uuid, type: a.type })) : value,
										)}
										multiple={true}
										agreements={agreements}
									/>
								)}
							/>
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "calculationMethod.tiers",
			render: () => tierUnit && (
				<TierForm
					currency={currency}
					index={index} />
			),
		},
	];

	return (
		<>
			{TieredCalculationMethodFormFields
				.filter(x => !hiddenFields?.includes(x.id))
				.map(x => (
					<Fragment key={x.id}>
						{x.render()}
					</Fragment>
				))
			}
		</>
	);
};

export default TieredCalculationMethodForm;
